import { inject } from '@angular/core';
import { HttpContextToken, HttpEvent, HttpHandlerFn, HttpRequest } from '@angular/common/http';
import { Params, Router } from '@angular/router';
import { from, mergeMap, Observable } from 'rxjs';
import { concatQueryParams } from '../utils/http';
import { AuthService } from '@auth0/auth0-angular';
import { AuthorizationService } from '../services/authorization.service';
import { StateService } from '../services/state.service';
import { RoutePaths } from '../shared/types/common';
import { TokenService } from '../services/token.service';
import { FreemiumApiPaths } from '../services/types';

export const SKIP_AUTH = new HttpContextToken<boolean>(() => false);

export enum ALLOWED_PAGE_QUERY_PARAMS {
  DEMO_DATA = 'demoData',
  COPILOT_DEBUG = 'coDebug' // Copilot debug flag forwarded to the server copilot endpoint
}

const DEMO_USER_EMAIL = "hubdemo@browsi.com";
const DEMO_ACCOUNT_KEY = "hubdemo";

let isCopilotDebug = false;

export function httpInterceptor(req: HttpRequest<unknown>, next: HttpHandlerFn):  Observable<HttpEvent<unknown>> {
  const router = inject(Router);
  const auth = inject(AuthService);
  const authorization = inject(AuthorizationService);
  const state =inject(StateService);
  const tokenService = inject(TokenService);
  const queryParamsToAdd: Params = {};
  const pageQueryParams = router.parseUrl(router.url).queryParams;
  
  const processRequest = (token: string | null) => {
    const headers: { [key: string]: string } = {};
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    const authReq = req.clone({ setHeaders: headers });
    
    Object.values(ALLOWED_PAGE_QUERY_PARAMS).forEach((value) => {
      const isDemoDataForDemoUser = (authorization.userProfile.email === DEMO_USER_EMAIL || state.activeAccount() === DEMO_ACCOUNT_KEY) && value === ALLOWED_PAGE_QUERY_PARAMS.DEMO_DATA;
      if (pageQueryParams[value] || isDemoDataForDemoUser) {
        queryParamsToAdd[value] = (isDemoDataForDemoUser && !pageQueryParams[value]) ? "true" : pageQueryParams[value];
      }
    });
    const body = req.body as any;
    if (state.accounts.length === 0 && req.url.includes(RoutePaths.CAMPAIGNS) || body?.accountKeys?.includes(DEMO_ACCOUNT_KEY)) {
      queryParamsToAdd[ALLOWED_PAGE_QUERY_PARAMS.DEMO_DATA] = "true";
    }
    if (pageQueryParams[ALLOWED_PAGE_QUERY_PARAMS.COPILOT_DEBUG] || isCopilotDebug) {
      queryParamsToAdd[ALLOWED_PAGE_QUERY_PARAMS.COPILOT_DEBUG] = isCopilotDebug? "true" :pageQueryParams[ALLOWED_PAGE_QUERY_PARAMS.COPILOT_DEBUG];
      isCopilotDebug = true;
    }
    if(!req.url.includes('copilot')){
      isCopilotDebug = false;
    }

    const updatedUrl = concatQueryParams(req.url, queryParamsToAdd);
    const clonedRequest = authReq.clone({ url: updatedUrl });
    return next(clonedRequest);
  };
  
  const skipAuth = req.context.get(SKIP_AUTH);
  if (skipAuth) {
    return processRequest(null);
  }

  if (Object.values(FreemiumApiPaths).some(path => req.url.includes(path))) {
    const storedToken = tokenService.getToken();
    if (storedToken) {
      return processRequest(storedToken);
    }
  }
  
  return from(auth.getAccessTokenSilently()).pipe(
    mergeMap((token) => processRequest(token))
  );
}
